import { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PublicJobsListPreview } from "@/components/admin/PublicJobsListPreview";
import { PublicJobPagePreview } from "@/components/admin/PublicJobPagePreview";

const PublicJobsTab = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const { toast } = useToast();
  const [publicJobsMainColor, setPublicJobsMainColor] = useState("#0057ff");
  const [publicJobsBgColor, setPublicJobsBgColor] = useState("#ffffff");
  const [publicJobsTextColor, setPublicJobsTextColor] = useState("#222222");
  const [publicJobsTitle, setPublicJobsTitle] = useState("");
  const [publicJobsHeadline, setPublicJobsHeadline] = useState("");
  const [publicJobsUrl, setPublicJobsUrl] = useState("");
  const [publicJobsPageStyle, setPublicJobsPageStyle] = useState<"square" | "rounded">("rounded");
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState<'list' | 'job'>('list');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    supabase
      .from("custom_publicstyle")
      .select("customstyle")
      .eq("organization_id", currentOrganization.id)
      .single()
      .then(({ data, error }) => {
        if (data && data.customstyle) {
          setPublicJobsMainColor(data.customstyle.mainColor || "#0057ff");
          setPublicJobsBgColor(data.customstyle.bgColor || "#ffffff");
          setPublicJobsTextColor(data.customstyle.textColor || "#222222");
          setPublicJobsTitle(data.customstyle.title || "");
          setPublicJobsHeadline(data.customstyle.headline || "");
          setPublicJobsUrl(data.customstyle.jobsUrl || "");
          setPublicJobsPageStyle(data.customstyle.pageStyle || "rounded");
          setLogoUrl(data.customstyle.logoUrl || "");
        }
        // Optionally handle error
      });
  }, [currentOrganization?.id]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.tabs.publicJobs", "Public Jobs")}</CardTitle>
        <CardDescription>
          {t("admin.publicJobs.customizeDesc", "Customize the look of your public job pages below.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6 max-w-xl" onSubmit={async (e) => {
          e.preventDefault();
          if (!currentOrganization?.id) {
            toast({ title: "No organization selected", description: "Please select an organization.", variant: "destructive" });
            return;
          }
          setSaving(true);
          const customstyle = {
            mainColor: publicJobsMainColor,
            bgColor: publicJobsBgColor,
            textColor: publicJobsTextColor,
            title: publicJobsTitle,
            headline: publicJobsHeadline,
            jobsUrl: publicJobsUrl,
            pageStyle: publicJobsPageStyle,
            logoUrl: logoUrl,
          };
          const { error } = await supabase
            .from("custom_publicstyle")
            .upsert([
              {
                organization_id: currentOrganization.id,
                customstyle,
              },
            ], { onConflict: "organization_id" });
          setSaving(false);
          if (error) {
            toast({ title: "Failed to save", description: error.message, variant: "destructive" });
          } else {
            toast({ title: "Saved!", description: "Your public jobs style has been saved.", duration: 3000 });
          }
        }}>
          {/* Main Colour */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.mainColor", "Main Colour")}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={publicJobsMainColor}
                onChange={e => setPublicJobsMainColor(e.target.value)}
                className="w-8 h-8 p-0 border-none bg-transparent"
                aria-label="Main Colour"
              />
              <Input
                value={publicJobsMainColor}
                onChange={e => setPublicJobsMainColor(e.target.value)}
                className="w-32"
                maxLength={7}
                aria-label="Main Colour Hex"
              />
              <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Main Colour" onClick={() => setPublicJobsMainColor("#0057ff")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
            </div>
          </div>
          {/* Background Colour */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.bgColor", "Background Colour")}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={publicJobsBgColor}
                onChange={e => setPublicJobsBgColor(e.target.value)}
                className="w-8 h-8 p-0 border-none bg-transparent"
                aria-label="Background Colour"
              />
              <Input
                value={publicJobsBgColor}
                onChange={e => setPublicJobsBgColor(e.target.value)}
                className="w-32"
                maxLength={7}
                aria-label="Background Colour Hex"
              />
              <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Background Colour" onClick={() => setPublicJobsBgColor("#ffffff")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
            </div>
          </div>
          {/* Title Colour */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.titleColor", "Title Colour")}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={publicJobsTextColor}
                onChange={e => setPublicJobsTextColor(e.target.value)}
                className="w-8 h-8 p-0 border-none bg-transparent"
                aria-label="Title Colour"
              />
              <Input
                value={publicJobsTextColor}
                onChange={e => setPublicJobsTextColor(e.target.value)}
                className="w-32"
                maxLength={7}
                aria-label="Title Colour Hex"
              />
              <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Title Colour" onClick={() => setPublicJobsTextColor("#222222")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
            </div>
          </div>
          {/* Title */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.title", "Title")}</label>
            <Input
              value={publicJobsTitle}
              onChange={e => setPublicJobsTitle(e.target.value)}
              className="w-full"
              aria-label="Title"
              disabled={publicJobsTitle === ' '}
            />
            <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Title" onClick={() => setPublicJobsTitle("")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
            <label className="flex items-center gap-1 ml-2 text-xs">
              <input
                type="checkbox"
                checked={publicJobsTitle === ' '}
                onChange={e => setPublicJobsTitle(e.target.checked ? ' ' : '')}
              />
              {t("admin.publicJobs.hide", "Hide")}
            </label>
          </div>
          {/* Headline */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.headline", "Headline")}</label>
            <Input
              value={publicJobsHeadline}
              onChange={e => setPublicJobsHeadline(e.target.value)}
              className="w-full"
              aria-label="Headline"
              disabled={publicJobsHeadline === ' '}
            />
            <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Headline" onClick={() => setPublicJobsHeadline("")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
            <label className="flex items-center gap-1 ml-2 text-xs">
              <input
                type="checkbox"
                checked={publicJobsHeadline === ' '}
                onChange={e => setPublicJobsHeadline(e.target.checked ? ' ' : '')}
              />
              {t("admin.publicJobs.hide", "Hide")}
            </label>
          </div>
          {/* Jobs URL */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.jobsUrl", "Jobs URL")}</label>
            <Input
              value={publicJobsUrl}
              onChange={e => setPublicJobsUrl(e.target.value)}
              className="w-full"
              aria-label="Jobs URL"
            />
            <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Jobs URL" onClick={() => setPublicJobsUrl("")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
          </div>
          {/* Page Style */}
          <div className="mb-4">
            <label className="block font-medium mb-2">{t("admin.publicJobs.pageStyle", "Page Style")}</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pageStyle"
                  value="rounded"
                  checked={publicJobsPageStyle === "rounded"}
                  onChange={(e) => setPublicJobsPageStyle(e.target.value as "rounded" | "square")}
                  className="w-4 h-4 text-blue-500 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                />
                <span className="text-sm">{t("admin.publicJobs.rounded", "Rounded")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pageStyle"
                  value="square"
                  checked={publicJobsPageStyle === "square"}
                  onChange={(e) => setPublicJobsPageStyle(e.target.value as "rounded" | "square")}
                  className="w-4 h-4 text-blue-500 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                />
                <span className="text-sm">{t("admin.publicJobs.square", "Square")}</span>
              </label>
            </div>
          </div>
          {/* Custom Logo */}
          <div className="mb-4 flex items-center gap-2">
            <label className="block font-medium mb-1">{t("admin.publicJobs.customLogo", "Custom Logo")}</label>
            <div className="flex items-center gap-4">
              {logoUrl && (
                <img src={logoUrl} alt="Logo Preview" className="h-12 w-auto rounded border" />
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !currentOrganization?.id) return;
                  const fileExt = file.name.split('.').pop();
                  const filePath = `${currentOrganization.id}.${fileExt}`;
                  setUploadingLogo(true);
                  const { error: uploadError } = await supabase.storage
                    .from("public-logos")
                    .upload(filePath, file, { upsert: true });
                  if (uploadError) {
                    toast({
                      title: t("admin.publicJobs.logoUploadError", "Logo upload failed"),
                      description: uploadError.message,
                      variant: "destructive",
                    });
                    setUploadingLogo(false);
                    return;
                  }
                  const { data: urlData } = supabase.storage
                    .from("public-logos")
                    .getPublicUrl(filePath);
                  setLogoUrl(urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : "");
                  setUploadingLogo(false);
                }}
              />
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {t("admin.publicJobs.uploadLogo", "Upload Logo")}
              </button>
              {logoUrl && (
                <button type="button" className="ml-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Clear Logo" onClick={() => setLogoUrl("")}> <Trash2 className="w-4 h-4 text-gray-400" /> </button>
              )}
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
              {t("admin.publicJobs.preview", "Preview")}
            </Button>
            <Button type="submit" className="bg-blue-500 text-white" disabled={saving || uploadingLogo}>
              {saving ? t("admin.publicJobs.saving", "Saving...") : t("admin.publicJobs.save", "Save")}
            </Button>
          </div>
        </form>
      </CardContent>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-full p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>{t("admin.publicJobs.previewTitle", "Public Page Preview")}</DialogTitle>
            <div className="flex gap-2 mt-2 justify-center">
              <Button size="sm" variant={previewPage === 'list' ? 'default' : 'outline'} onClick={() => setPreviewPage('list')}>
                {t("admin.publicJobs.previewList", "Jobs List")}
              </Button>
              <Button size="sm" variant={previewPage === 'job' ? 'default' : 'outline'} onClick={() => setPreviewPage('job')}>
                {t("admin.publicJobs.previewJob", "Job Page")}
              </Button>
            </div>
          </DialogHeader>
          <div className="bg-gray-50 dark:bg-gray-800/50 p-6 overflow-auto" style={{ minHeight: 500, maxHeight: '80vh' }}>
            {previewPage === 'list' ? (
              <PublicJobsListPreview
                customStyle={{
                  mainColor: publicJobsMainColor,
                  bgColor: publicJobsBgColor,
                  textColor: publicJobsTextColor,
                  title: publicJobsTitle,
                  headline: publicJobsHeadline,
                  jobsUrl: publicJobsUrl,
                  pageStyle: publicJobsPageStyle,
                  logoUrl: logoUrl,
                }}
              />
            ) : (
              <PublicJobPagePreview
                customStyle={{
                  mainColor: publicJobsMainColor,
                  bgColor: publicJobsBgColor,
                  textColor: publicJobsTextColor,
                  title: publicJobsTitle,
                  headline: publicJobsHeadline,
                  jobsUrl: publicJobsUrl,
                  pageStyle: publicJobsPageStyle,
                  logoUrl: logoUrl,
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PublicJobsTab; 